import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useContext, useEffect, useRef, useState } from 'react';
import { ConnectionState } from 'types/pushNotifications';
import { getNegotiatedConnection } from 'utils/signalrConnector';

interface HubConnectionInfo {
  hubConnection: HubConnection | null;
  connectionState: ConnectionState;
}

const useHubConnection = (hubName: string): HubConnectionInfo => {
  const { getAuthToken } = useContext(AuthContext);
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Connecting
  );
  const isInitializingRef = useRef(false);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializingRef.current || isInitializedRef.current || !hubName) {
      return;
    }

    isInitializingRef.current = true;

    const hubConnection = getNegotiatedConnection(hubName, getAuthToken);

    setHubConnection(hubConnection);

    hubConnection.onclose(() => {
      console.log('Hub closed:', hubName);
      setConnectionState(ConnectionState.Disconnected);
    });

    hubConnection.onreconnecting(() => {
      console.log('Hub reconnecting:', hubName);
      setConnectionState(ConnectionState.Reconnecting);
    });

    hubConnection.onreconnected(() => {
      console.log('Hub reconnected:', hubName);
      setConnectionState(ConnectionState.Connected);
    });

    const startConnection = async () => {
      try {
        if (hubConnection.state === HubConnectionState.Disconnected) {
          await hubConnection.start();
          console.log('Hub started:', hubName);
          setConnectionState(ConnectionState.Connected);
        } else if (hubConnection.state === HubConnectionState.Connected) {
          console.log('Hub already connected:', hubName);
          setConnectionState(ConnectionState.Connected);
        }
      } catch (err) {
        console.error('Error starting hub connection:', hubName, err);
        setConnectionState(ConnectionState.Disconnected);
      } finally {
        isInitializingRef.current = false;
        isInitializedRef.current = true;
      }
    };

    startConnection();

    return () => {
      console.log('Cleaning up hub connection:', hubName);
      if (hubConnection.state !== HubConnectionState.Disconnected) {
        hubConnection.stop().catch((err) => {
          console.error('Error stopping hub connection:', err);
        });
      }
      isInitializingRef.current = false;
      isInitializedRef.current = false;
      setHubConnection(null);
    };
  }, [hubName, getAuthToken]);

  return {
    hubConnection,
    connectionState,
  };
};

export default useHubConnection;
